/* Admin operation control centre for the Leave / Reschedule / Replacement system.
   Data comes from /admin/leave-reschedule-dashboard — this page is presentation only.
   Policy settings (Super Admin) edit /admin/leave-reschedule-settings. */
import { useState } from "react";
import { Link } from "react-router-dom";
import api, { useList, errMsg } from "@/lib/api";
import { PageHeader, StatCard, StatusBadge, Spinner, EmptyState, QueueCard, QueueRow } from "@/components/common";
import { CategoryBadge } from "@/pages/operations/LeavePages";
import { SourceBadge } from "@/pages/reschedule/ReschedulePages";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plane, AlertTriangle, BellRing, Hourglass, MessagesSquare, Eye, Timer,
  Settings2, ClipboardList, Check,
} from "lucide-react";

const NUM_SETTINGS = [
  ["quota_per_cycle", "Requests per student per 12-month cycle"],
  ["standard_notice_days", "Standard leave notice (days)"],
  ["max_negotiation_rounds", "Max proposal rounds before direct discussion"],
  ["min_standalone_minutes", "Minimum standalone lesson (minutes)"],
  ["extension_block_minutes", "Extension block size (minutes)"],
  ["ack_escalation_imminent_minutes", "Escalate if class within 2h (minutes)"],
  ["ack_escalation_same_day_minutes", "Escalate same-day (minutes)"],
  ["ack_escalation_future_minutes", "Escalate future classes (minutes)"],
];
const BOOL_SETTINGS = [
  ["mc_evidence_required", "MC requires medical evidence"],
  ["el_evidence_required", "Emergency leave requires evidence"],
  ["group_student_replacement_enabled", "Allow student replacement for group classes"],
];

function SettingsDialog({ open, onOpenChange }) {
  const { data: settings, loading, reload } = useList(open ? "/admin/leave-reschedule-settings" : null, [open]);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const val = (k) => (draft[k] !== undefined ? draft[k] : settings?.[k]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/leave-reschedule-settings", draft);
      toast.success("Policy settings saved");
      setDraft({});
      reload();
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="lr-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Leave & replacement policy</DialogTitle>
          <DialogDescription>School-wide rules — changes apply to new requests immediately.</DialogDescription>
        </DialogHeader>
        {loading || !settings ? <Spinner /> : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {NUM_SETTINGS.map(([k, label]) => (
                <div key={k} className="space-y-1">
                  <Label className="text-xs font-semibold text-muted-foreground">{label}</Label>
                  <Input type="number" value={val(k) ?? ""} data-testid={`setting-${k}`}
                    onChange={(e) => setDraft((d) => ({ ...d, [k]: parseInt(e.target.value || 0, 10) }))} />
                </div>
              ))}
            </div>
            <div className="space-y-2">
              {BOOL_SETTINGS.map(([k, label]) => (
                <div key={k} className="flex items-center justify-between rounded-md border p-3">
                  <Label className="text-sm">{label}</Label>
                  <Switch checked={!!val(k)} data-testid={`setting-${k}`}
                    onCheckedChange={(v) => setDraft((d) => ({ ...d, [k]: v }))} />
                </div>
              ))}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy || Object.keys(draft).length === 0} data-testid="save-settings-button">
            {busy ? "Saving..." : "Save policy"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function LeaveRescheduleDashboard() {
  const { data, loading, reload } = useList("/admin/leave-reschedule-dashboard?include=lists");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const acknowledge = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/reschedule-requests/${id}/acknowledge`);
      toast.success("Acknowledged — monitoring record saved");
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusyId(null); }
  };

  if (loading || !data) return <Spinner full />;
  const q = data.queues || {};
  const exceptions = data.pending_evidence + data.admin_exception + data.quota_exceeded;

  return (
    <div data-testid="leave-reschedule-dashboard">
      <PageHeader title="Leave & Replacement Control Centre"
        description="Approve leave and handle exceptions — mutually agreed replacements confirm themselves. You only monitor."
        actions={
          <Button variant="outline" onClick={() => setSettingsOpen(true)} data-testid="open-settings-button">
            <Settings2 className="h-4 w-4 mr-1" />Policy settings
          </Button>
        } />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 stagger-in">
        <StatCard icon={Plane} label="Leave to approve" value={data.pending_leave_approval} accent testId="stat-pending-leave" />
        <StatCard icon={AlertTriangle} label="Exceptions" value={exceptions}
          sub="late notice · quota · evidence" testId="stat-exceptions" />
        <StatCard icon={BellRing} label="Unacknowledged" value={data.unacknowledged_notices}
          sub={`${data.escalated_notices} escalated`} testId="stat-unack" />
        <StatCard icon={Hourglass} label="Awaiting proposal" value={data.awaiting_teacher_proposal} testId="stat-awaiting" />
        <StatCard icon={MessagesSquare} label="Direct discussion" value={data.pending_direct_discussion} testId="stat-direct" />
        <StatCard icon={Eye} label="To acknowledge" value={data.auto_confirmed_unacknowledged}
          sub="auto-confirmed" testId="stat-auto-confirmed" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        <QueueCard title="Leave waiting for approval" icon={Plane} count={data.pending_leave_approval}
          testId="queue-pending-leave">
          {(q.pending_leave || []).length === 0 ? <EmptyState text="No leave requests waiting." /> :
            q.pending_leave.map((l) => (
              <QueueRow key={l.id} to="/admin/leave-requests"
                primary={<>{l.teacher_name || l.student_name} · <span className="font-mono">{l.leave_date}</span></>}
                secondary={l.reason}
                right={<><CategoryBadge category={l.leave_category || "standard"} />
                  <span className="text-xs px-2 py-0.5 rounded-full bg-muted capitalize">{l.leave_type}</span></>}
                testId={`queue-leave-${l.id}`} />
            ))}
        </QueueCard>

        <QueueCard title="Exceptions needing your decision" icon={AlertTriangle} count={exceptions}
          tone={exceptions > 0 ? "danger" : "default"} testId="queue-exceptions">
          {(q.exceptions || []).length === 0 ? <EmptyState text="No exceptions. The system is handling normal cases." /> :
            q.exceptions.map((l) => (
              <QueueRow key={l.id} to="/admin/leave-requests"
                primary={<>{l.teacher_name || l.student_name} · <span className="font-mono">{l.leave_date}</span></>}
                secondary={l.exception_reason || l.reason}
                right={<StatusBadge status={l.status} />}
                testId={`queue-exception-${l.id}`} />
            ))}
        </QueueCard>

        <QueueCard title="Parents who have NOT acknowledged (MC/EL)" icon={BellRing}
          count={data.unacknowledged_notices}
          tone={data.unacknowledged_notices > 0 ? "warning" : "default"} testId="queue-unack">
          {(q.unacknowledged_notices || []).length === 0 ? <EmptyState text="All notices acknowledged." /> :
            q.unacknowledged_notices.map((n) => (
              <QueueRow key={n.id}
                primary={<>{n.student_name} · <span className="font-mono">{n.class_date} {n.start_time}</span></>}
                secondary={`${n.course_name} · Teacher ${n.teacher_name} — contact the family directly`}
                right={<>{n.escalated && <StatusBadge status="urgent" />}<CategoryBadge category={n.leave_category} /></>}
                testId={`queue-notice-${n.id}`} />
            ))}
        </QueueCard>

        <QueueCard title="Waiting for teacher's replacement proposal" icon={Hourglass}
          count={data.awaiting_teacher_proposal} testId="queue-awaiting">
          {(q.awaiting_teacher_proposal || []).length === 0 ? <EmptyState text="No replacement requests waiting." /> :
            q.awaiting_teacher_proposal.map((r) => (
              <QueueRow key={r.id} to={`/admin/reschedule-approval/${r.id}`}
                primary={<>{r.student_name} · {r.course_name}</>}
                secondary={`${r.teacher_name} · missed ${r.original_date} ${r.original_start_time}`}
                right={<SourceBadge source={r.source_type} />}
                testId={`queue-awaiting-${r.id}`} />
            ))}
        </QueueCard>

        <QueueCard title="Pending direct discussion (3 rounds used)" icon={MessagesSquare}
          count={data.pending_direct_discussion} testId="queue-direct">
          {(q.pending_direct_discussion || []).length === 0 ? <EmptyState text="No stuck negotiations." /> :
            q.pending_direct_discussion.map((r) => (
              <QueueRow key={r.id} to={`/admin/reschedule-approval/${r.id}`}
                primary={<>{r.student_name} · {r.course_name}</>}
                secondary={`${r.teacher_name} · you can enter the final arrangement once agreed`}
                right={<StatusBadge status={r.status} />}
                testId={`queue-direct-${r.id}`} />
            ))}
        </QueueCard>

        <QueueCard title="Auto-confirmed — acknowledge only" icon={Check}
          count={data.auto_confirmed_unacknowledged} testId="queue-auto-confirmed">
          {(q.auto_confirmed || []).length === 0 ? <EmptyState text="Nothing to acknowledge." /> :
            q.auto_confirmed.map((r) => (
              <QueueRow key={r.id}
                primary={<>{r.student_name} · {r.course_name}</>}
                secondary={`Confirmed ${r.final_agreed_date} ${r.final_agreed_start_time}–${r.final_agreed_end_time} · ${r.confirmed_via === "direct_confirmation" ? "entered directly" : "mutual agreement"}`}
                right={
                  <Button size="sm" variant="outline" disabled={busyId === r.id}
                    onClick={() => acknowledge(r.id)} data-testid={`ack-auto-${r.id}`}>
                    <Eye className="h-3.5 w-3.5 mr-1" />Acknowledge
                  </Button>
                }
                testId={`queue-auto-${r.id}`} />
            ))}
        </QueueCard>

        <QueueCard title="Partially replaced — minutes still owed" icon={Timer}
          count={data.partially_replaced} testId="queue-partial">
          {(q.partially_replaced || []).length === 0 ? <EmptyState text="No outstanding replacement minutes." /> :
            q.partially_replaced.map((r) => (
              <QueueRow key={r.id} to={`/admin/reschedule-approval/${r.id}`}
                primary={<>{r.student_name} · {r.course_name}</>}
                secondary={`${r.minutes_completed}/${r.minutes_owed} min replaced — ${r.minutes_remaining} min remaining (never expires)`}
                right={<StatusBadge status="partially_replaced" />}
                testId={`queue-partial-${r.id}`} />
            ))}
        </QueueCard>

        <QueueCard title="Long-pending (14+ days without movement)" icon={ClipboardList}
          count={(q.long_pending || []).length} testId="queue-stale">
          {(q.long_pending || []).length === 0 ? <EmptyState text="No stale requests." /> :
            q.long_pending.map((r) => (
              <QueueRow key={r.id} to={`/admin/reschedule-approval/${r.id}`}
                primary={<>{r.student_name} · {r.course_name}</>}
                secondary={`Last update ${String(r.updated_at || "").slice(0, 10)}`}
                right={<StatusBadge status={r.status} />}
                testId={`queue-stale-${r.id}`} />
            ))}
        </QueueCard>
      </div>

      <p className="text-xs text-muted-foreground mt-6">
        Legacy queue: {data.legacy_pending_admin_approval} request(s) still in the old admin-approval state —{" "}
        <Link to="/admin/reschedule-requests" className="text-secondary underline">review them here</Link>.
      </p>

      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
    </div>
  );
}
